import _extends from "@babel/runtime/helpers/esm/extends";

/**
 * Polyfill for EventTarget.
 * This file is necessary when trying to inherit from EventTarget because
 * some environments doesn't have EventTarget (old browser or Node.js).
 */
// Types
const _listeners = Symbol('[[Listeners]]'); // Use a symbol to avoid property collision


export default class EventTarget {
  constructor() {
    this[_listeners] = {};
  }

  /**
   * Add a listener for the given event type.
   * The listener will be invoked when the event is dispatched.
   * @param type The event type for which to add a listener.
   * @param listener The listener to add.
   * @param [options] An optional list of options.
   * @param [options.once=false] If set to true, the listener will be removed once invoked.
   */
  addEventListener(type, listener, options) {
    // Make sure we always have a listeners map (e.g. when using EventTarget.prototype.addEventListener.call)
    if (!this[_listeners]) {
      this[_listeners] = {};
    }

    let signalListener;

    if (options != null && options.signal) {
      signalListener = () => this.removeEventListener(type, listener);

      options.signal.addEventListener('abort', signalListener);
    }

    const listeners = this[_listeners][type] || [];
    this[_listeners][type] = [...listeners, [listener, _extends({}, options, {
      signalListener
    })]];
  }
  /**
   * Dispatches an event to target.
   * @param event The event to dispatch.
   */


  dispatchEvent(event) {
    // Make sure we always have a listeners map (e.g. when using EventTarget.prototype.dispatchEvent.call)
    if (!this[_listeners]) {
      this[_listeners] = {};
    }

    event = event;
    Object.defineProperty(event, 'target', {
      value: this
    }); // Patch event if it doesn't have a non-standard `canPropagate` property (e.g. native event)

    if (!('canPropagate' in event)) {
      const originalStopImmediatePropagation = event.stopImmediatePropagation || (() => undefined);

      let canPropagate = true;
      Object.defineProperty(event, 'stopImmediatePropagation', {
        value() {
          canPropagate = false;
          originalStopImmediatePropagation.call(event);
        }

      });
      Object.defineProperty(event, 'canPropagate', {
        get: () => canPropagate
      });
    }

    const type = event.type;
    const listeners = this[_listeners][type] || [];

    for (const [listener, options] of listeners) {
      if (options != null && options.once) {
        this.removeEventListener(type, listener);
      }

      listener.call(this, event);

      if (!event.canPropagate) {
        break;
      }
    }

    return false;
  }
  /**
   * Removes the event listener in target's event listener list with the same
   * type and listener.
   * @param type The event type for which to remove a listener.
   * @param listener The listener to remove.
   */


  removeEventListener(type, listener) {
    // Make sure we always have a listeners map (e.g. when using EventTarget.prototype.removeEventListener.call)
    if (!this[_listeners]) {
      this[_listeners] = {};
    }

    if (!this[_listeners][type]) {
      return;
    }

    const listeners = this[_listeners][type];
    this[_listeners][type] = listeners.filter(([l, options]) => {
      if (listener === l) {
        if (options.signalListener) {
          var _options$signal;

          (_options$signal = options.signal) == null ? void 0 : _options$signal.removeEventListener('abort', options.signalListener);
        }

        return false;
      }

      return true;
    });
  }

}
//# sourceMappingURL=EventTarget.js.map